import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The one world transform. Both layers that matter — the DOM zone/sticky layer
 * and (from Milestone 4) the canvas drawing layer — read from this, which is
 * the whole reason it is hand-rolled rather than handed to a library: keeping a
 * third-party DOM transform in sync with a separately-transformed canvas costs
 * more than the hundred lines it saves.
 */
export type Transform = { x: number; y: number; scale: number };

export const ZOOM = { min: 0.1, max: 3, step: 1.15 } as const;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export type Viewport = { width: number; height: number };

export function useCanvasTransform(surface: React.RefObject<HTMLElement | null>) {
  const [transform, setTransform] = useState<Transform>({ x: 0, y: 0, scale: 1 });
  const [panning, setPanning] = useState(false);
  const [pinching, setPinching] = useState(false);
  const transformRef = useRef(transform);
  const pan = useRef<{ pointer: number; startX: number; startY: number; origin: Transform } | null>(
    null,
  );
  const touches = useRef(new Map<number, { x: number; y: number; canvas: boolean }>());
  const pinch = useRef<{
    pointers: [number, number];
    distance: number;
    worldX: number;
    worldY: number;
  } | null>(null);

  transformRef.current = transform;

  /** Screen pixels → world units. Everything stored is in world units. */
  const screenToWorld = useCallback(
    (clientX: number, clientY: number) => {
      const box = surface.current?.getBoundingClientRect();
      const left = box?.left ?? 0;
      const top = box?.top ?? 0;
      return {
        x: (clientX - left - transform.x) / transform.scale,
        y: (clientY - top - transform.y) / transform.scale,
      };
    },
    [surface, transform],
  );

  /** Zoom about a fixed screen point, so the thing under the cursor stays put. */
  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    setTransform((current) => {
      const next = clamp(current.scale * factor, ZOOM.min, ZOOM.max);
      if (next === current.scale) return current;

      const box = surface.current?.getBoundingClientRect();
      const anchorX = (clientX ?? (box ? box.left + box.width / 2 : 0)) - (box?.left ?? 0);
      const anchorY = (clientY ?? (box ? box.top + box.height / 2 : 0)) - (box?.top ?? 0);

      const worldX = (anchorX - current.x) / current.scale;
      const worldY = (anchorY - current.y) / current.scale;
      return { scale: next, x: anchorX - worldX * next, y: anchorY - worldY * next };
    });
  }, [surface]);

  const zoomIn = useCallback(() => zoomAt(ZOOM.step), [zoomAt]);
  const zoomOut = useCallback(() => zoomAt(1 / ZOOM.step), [zoomAt]);
  const setView = useCallback((next: Transform) => setTransform(next), []);

  /**
   * Frame a world-space box in the viewport, with a little breathing room.
   * Returns false when the surface has not been laid out yet — callers must not
   * treat that as a fit, or an unmeasured viewport produces a negative scale
   * that clamps to ZOOM.min and lands the board at 10%.
   */
  const fitTo = useCallback(
    (box: { x: number; y: number; width: number; height: number } | null): boolean => {
      const el = surface.current;
      if (!el) return false;
      const view = el.getBoundingClientRect();
      if (view.width <= 0 || view.height <= 0) return false;
      if (!box || box.width <= 0 || box.height <= 0) {
        setTransform({ x: 0, y: 0, scale: 1 });
        return true;
      }
      // Breathing room scales with the viewport so it cannot exceed it.
      const margin = Math.min(64, view.width * 0.06, view.height * 0.06);
      const scale = clamp(
        Math.min(
          (view.width - margin * 2) / box.width,
          (view.height - margin * 2) / box.height,
        ),
        ZOOM.min,
        ZOOM.max,
      );
      setTransform({
        scale,
        x: view.width / 2 - (box.x + box.width / 2) * scale,
        y: view.height / 2 - (box.y + box.height / 2) * scale,
      });
      return true;
    },
    [surface],
  );

  // Wheel must be a non-passive listener to preventDefault, which React's
  // onWheel cannot guarantee — so it is bound by hand.
  useEffect(() => {
    const el = surface.current;
    if (!el) return;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      // The wheel zooms, about the cursor. Shift+wheel pans sideways, for the
      // one case a wheel is still the natural way to move across the board.
      if (event.shiftKey) {
        setTransform((t) => ({ ...t, x: t.x - event.deltaY - event.deltaX }));
        return;
      }
      // A trackpad reports many small deltas where a mouse reports one large
      // one, so scale the step by the delta rather than stepping a fixed amount.
      const intensity = Math.min(Math.abs(event.deltaY), 60) / 60;
      const factor = event.deltaY < 0 ? 1 + 0.18 * intensity : 1 / (1 + 0.18 * intensity);
      zoomAt(factor, event.clientX, event.clientY);
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [surface, zoomAt]);

  /** Begin a pan. Callers decide what counts as empty canvas. */
  const startPan = useCallback(
    (event: React.PointerEvent) => {
      if (pinch.current) return;
      pan.current = {
        pointer: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        origin: transform,
      };
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      setPanning(true);
    },
    [transform],
  );

  const movePan = useCallback((event: React.PointerEvent) => {
    const active = pan.current;
    if (!active || active.pointer !== event.pointerId) return;
    setTransform({
      scale: active.origin.scale,
      x: active.origin.x + (event.clientX - active.startX),
      y: active.origin.y + (event.clientY - active.startY),
    });
  }, []);

  const endPan = useCallback((event: React.PointerEvent) => {
    if (pan.current?.pointer !== event.pointerId) return;
    pan.current = null;
    setPanning(false);
  }, []);

  const cancelPan = useCallback((pointerId?: number) => {
    if (pointerId !== undefined && pan.current?.pointer !== pointerId) return;
    pan.current = null;
    if (!pinch.current) setPanning(false);
  }, []);

  /**
   * Track touch pointers before child controls see them. A two-finger gesture
   * starts only when both fingers began on canvas rather than on an object, so
   * pinching can never steal a sticky, attachment or resize handle mid-drag.
   */
  const touchStart = useCallback((event: React.PointerEvent, onCanvas: boolean): boolean => {
    if (event.pointerType !== "touch") return false;
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY, canvas: onCanvas });
    if (touches.current.size !== 2) return false;

    const pair = [...touches.current.entries()];
    const first = pair[0];
    const second = pair[1];
    if (!first || !second || !first[1].canvas || !second[1].canvas) return false;

    const centreX = (first[1].x + second[1].x) / 2;
    const centreY = (first[1].y + second[1].y) / 2;
    const distance = Math.hypot(second[1].x - first[1].x, second[1].y - first[1].y);
    if (distance < 1) return false;

    const box = surface.current?.getBoundingClientRect();
    const localX = centreX - (box?.left ?? 0);
    const localY = centreY - (box?.top ?? 0);
    const current = transformRef.current;
    pinch.current = {
      pointers: [first[0], second[0]],
      distance,
      worldX: (localX - current.x) / current.scale,
      worldY: (localY - current.y) / current.scale,
    };
    pan.current = null;
    setPinching(true);
    setPanning(true);
    const target = event.currentTarget as HTMLElement;
    for (const [id] of pair) target.setPointerCapture(id);
    return true;
  }, [surface]);

  /** Move and zoom together, keeping the board point between the fingers fixed. */
  const touchMove = useCallback((event: React.PointerEvent): boolean => {
    if (event.pointerType !== "touch") return false;
    const known = touches.current.get(event.pointerId);
    if (known) touches.current.set(event.pointerId, { ...known, x: event.clientX, y: event.clientY });
    const active = pinch.current;
    if (!active || !active.pointers.includes(event.pointerId)) return false;

    const first = touches.current.get(active.pointers[0]);
    const second = touches.current.get(active.pointers[1]);
    if (!first || !second) return true;
    const centreX = (first.x + second.x) / 2;
    const centreY = (first.y + second.y) / 2;
    const distance = Math.hypot(second.x - first.x, second.y - first.y);
    if (distance < 1 || active.distance < 1) return true;
    const box = surface.current?.getBoundingClientRect();
    const localX = centreX - (box?.left ?? 0);
    const localY = centreY - (box?.top ?? 0);
    const factor = distance / active.distance;
    setTransform((current) => {
      const next = clamp(current.scale * factor, ZOOM.min, ZOOM.max);
      return {
        scale: next,
        x: localX - active.worldX * next,
        y: localY - active.worldY * next,
      };
    });
    active.distance = distance;
    // The anchored world point stays the same while distance is rebased each
    // frame, preventing a delayed React render from making the pinch jump.
    return true;
  }, [surface]);

  const touchEnd = useCallback((event: React.PointerEvent): boolean => {
    if (event.pointerType !== "touch") return false;
    const active = pinch.current;
    const wasPinching = Boolean(active?.pointers.includes(event.pointerId));
    touches.current.delete(event.pointerId);
    if (wasPinching) {
      pinch.current = null;
      pan.current = null;
      setPinching(false);
      setPanning(false);
    }
    return wasPinching;
  }, []);

  return {
    transform,
    panning,
    pinching,
    screenToWorld,
    zoomIn,
    zoomOut,
    zoomAt,
    fitTo,
    setView,
    startPan,
    movePan,
    endPan,
    cancelPan,
    touchStart,
    touchMove,
    touchEnd,
  };
}
